import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, FolderOpen, Search } from 'lucide-react';
import { Button } from '../../components/common/Button';
import { categoriesApi } from '../../services/api';
import { Category } from '../../types';

interface CategoryListProps {
  onNavigate: (page: string, id?: string) => void;
}

export const CategoryList: React.FC<CategoryListProps> = ({ onNavigate }) => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleting, setDeleting] = useState<string | null>(null);

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      const response = await categoriesApi.getAll();
      if (response.success && response.data) {
        setCategories(response.data);
      }
    } catch (error) {
      console.error('Failed to load categories:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this category?')) return;

    setDeleting(id);
    try {
      const response = await categoriesApi.delete(id);
      if (response.success) {
        setCategories(categories.filter(cat => cat.id !== id));
      }
    } catch (error) {
      console.error('Failed to delete category:', error);
    } finally {
      setDeleting(null);
    }
  };

  const filteredCategories = categories.filter(category =>
    category.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Categories</h2>
          <p className="text-gray-600">Organize your products into categories</p>
        </div>
        <Button
          onClick={() => onNavigate('category-add')}
          icon={Plus}
          size="lg"
        >
          Add Category
        </Button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
        <input
          type="text"
          placeholder="Search categories..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      {/* Categories Grid */}
      {filteredCategories.length === 0 ? (
        <div className="text-center py-12">
          <FolderOpen className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {searchTerm ? 'No categories found' : 'No categories yet'}
          </h3>
          <p className="text-gray-600 mb-6">
            {searchTerm
              ? 'Try adjusting your search terms'
              : 'Get started by creating your first category'
            }
          </p>
          {!searchTerm && (
            <Button
              onClick={() => onNavigate('category-add')}
              icon={Plus}
            >
              Add First Category
            </Button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredCategories.map((category) => (
            <div
              key={category.id}
              className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow p-6"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3 flex-1">
                  <div className="bg-purple-100 p-2 rounded-lg">
                    <FolderOpen className="w-5 h-5 text-purple-600" />
                  </div>
                  <button
                    onClick={() => onNavigate('category-edit', category.id)}
                    className="font-semibold text-gray-900 hover:text-blue-600 transition-colors text-left flex-1 cursor-pointer"
                    title="Click to edit category"
                  >
                    {category.name}
                  </button>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => onNavigate('category-edit', category.id)}
                    className="text-gray-400 hover:text-blue-600 transition-colors"
                    title="Edit category"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(category.id)}
                    disabled={deleting === category.id}
                    className="text-gray-400 hover:text-red-600 transition-colors disabled:opacity-50"
                    title="Delete category"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <div className="text-xs text-gray-500">
                Created {category.created_at ? new Date(category.created_at).toLocaleDateString() : 'Unknown date'}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};